import React from 'react'
import { Route, withRouter } from 'react-router-dom'
import { Platform } from 'react-native'
import Auth from './config/okta'
import asyncComponent from './containers/AsyncComponent'
import { Security, LoginCallback } from '@okta/okta-react'
import { compose, ApolloProvider } from 'react-apollo'
import client from './apollo'
import { FourOhFour } from '@sukiai/components'
import {
  ROUTES,
  LO_STO,
  SESH_STO,
  isValidPathname,
  initializeUser
} from './lib'
import { isAgentDisabled, isAmwellTheme, logout } from './lib/util'
import loSto from './config/loSto'
import seshSto from './config/seshSto'
import {
  Agent,
  Help,
  Home,
  IntroTutorial,
  WindowsDownload,
  Toast,
  TopNav,
  Veil,
  EmbedScripts
} from './containers'
import { SecureRoute, WhatCanISayModal } from './components'
import withAnalytics from './analytics/withAnalytics'
import withEmbedAuthentication from './hoc/withEmbedAuthentication'
import EMRConnectionToolBar from './components/emrConnectionToolBar'
import isNil from 'lodash/isNil'

console.info('react-native platform:', Platform.OS)

const Root = asyncComponent(() => import('./containers/Root'))
const PatientNotes = asyncComponent(() => import('./containers/PatientNotes'))
const ResetPassword = asyncComponent(() =>
  import('./containers/ResetPassword')
)
const Note = asyncComponent(() => import('./containers/Note'))
const UnfinishedNotes = asyncComponent(() =>
  import('./containers/UnfinishedNotes')
)
const SelfOnboard = asyncComponent(() => import('./containers/SelfOnboard/'))
const SchedulePatient = asyncComponent(() =>
  import('./containers/SchedulePatient/')
)
const AltaisEntry = asyncComponent(() => import('./containers/Altais'))

const Routes = (props) => {
  const [state, setState] = React.useState({
    userLoaded: seshSto.session(SESH_STO.USER_ID)
  })

  React.useEffect(() => {
    if (isAgentDisabled() && window.sessionStorage) {
      seshSto.session(SESH_STO.DISABLE_AGENT, 'true')
    }

    if (!isAgentDisabled() && window.sessionStorage) {
      seshSto.session(SESH_STO.DISABLE_AGENT, 'false')
    }
  }, [window.location])

  const renderAlwaysOnComponents = (props) => (
    <Toast key='Toast' history={props.history} />
  )

  const authRenderAlwaysOnComponents = () => [
    <Agent location={window.location} key='Agent' history={props.history} />,
    <TopNav key='TopNav' history={props.history} />,
    <Veil key='Veil' history={props.history} />,
    <WhatCanISayModal key='WhatCanISayModal' history={props.history} />,
    <EMRConnectionToolBar key='EMRConnectionToolBar' />
  ]

  const loadUser = async () => {
    await initializeUser(Auth)
    setState((prevState) => ({ ...prevState, userLoaded: true }))
    if (!isAmwellTheme() && loSto.get(LO_STO.IS_FIRST_TIME_USER)) {
      props.history.push(ROUTES.INTRO_PAGE)
    }
  }

  const validateRoute = () => {
    const { history } = props
    if (!state.userLoaded) {
      loadUser()
      return null
    }

    // when path is register or windowsupdate do no include the authRenderAlwaysOnComponents
    if (window.location.pathname.includes('register')) {
      history.push(ROUTES.HOME)
      return null
    }

    if (window.location.pathname.includes('windowsupdate')) {
      return null
    }

    return isValidPathname(window.location.pathname) ? (
      authRenderAlwaysOnComponents()
    ) : (
      <FourOhFour />
    )
  }

  const logoutUser = () => {
    logout()
    return null
  }

  const checkFragment = () => {
    const { history } = props
    const hash = history?.location?.hash
    if (hash.includes('error=access_denied')) {
      loSto.set(LO_STO.ALTAIS_ERROR, true)
    }

    if (hash.includes('vState=2')) {
      history.push(ROUTES.HOME)
    }
    return null
  }

  return (
    <Security authService={Auth}>
      <ApolloProvider client={client}>
        {/* Authentication */}
        <Route exact path={ROUTES.ROOT} component={getComponent(Root)} />

        {/* Reset password */}
        <Route
          exact
          path={ROUTES.RESET_PASSWORD}
          component={getComponent(ResetPassword)}
        />

        {/* Registration routes */}
        <Route path={ROUTES.REGISTER} component={getComponent(SelfOnboard)} />
        <Route path={ROUTES.DEMO_APP} component={getComponent(SelfOnboard)} />

        {/* GCP Organization Registration route */}
        <Route
          path={ROUTES.GCP_REGISTER}
          component={getComponent(SelfOnboard)}
        />

        {/* Always on */}
        <Route path={ROUTES.ROOT} render={renderAlwaysOnComponents} />

        {/* Altais Entrypoint */}
        <Route
          path={ROUTES.ALTAIS_ENTRY}
          component={getComponent(AltaisEntry)}
        />

        {/* Always on for authenticated views */}
        <SecureRoute path={ROUTES.DOCTOR_VIEW} render={validateRoute} />

        {/* Download Windows App route */}
        <Route
          path={ROUTES.WINDOWS_UPDATE}
          component={getComponent(WindowsDownload)}
        />

        {/* Logout Route */}
        <Route path={ROUTES.LOGOUT} render={logoutUser} />

        {/* Contextual */}
        {state.userLoaded && [
          <SecureRoute
            exact
            path={ROUTES.SCHEDULE}
            component={getComponent(SchedulePatient)}
            key='schedule'
          />,
          <SecureRoute
            exact
            path={ROUTES.UNFINISHED_NOTES}
            component={getComponent(UnfinishedNotes)}
            key='unfinished-notes'
          />,
          <SecureRoute
            exact
            path={`${ROUTES.PATIENT_NOTES}/:patientId`}
            component={getComponent(PatientNotes)}
            key='patient-notes'
          />,
          <SecureRoute
            exact
            path={`${ROUTES.NOTE}/:id`}
            component={getComponent(Note)}
            key='note'
          />,
          <SecureRoute
            exact
            path={ROUTES.HELP}
            component={getComponent(Help)}
            key='help'
          />,
          <SecureRoute
            exact
            path={ROUTES.HOME}
            component={getComponent(Home)}
            key='home'
          />,
          <SecureRoute
            exact
            path={ROUTES.INTRO_PAGE}
            component={getComponent(IntroTutorial)}
            key='intro-tutorial'
          />,
          <SecureRoute
            exact
            path={`${ROUTES.SCRIPTS}/:emr`}
            component={getComponent(EmbedScripts)}
            key='embed-scritps'
          />
        ]}

        {/* Developer */}
        <Route path={ROUTES.IMPLICIT_CALLBACK} component={LoginCallback} />
        <Route path={ROUTES.IMPLICIT_CALLBACK} render={checkFragment} />
      </ApolloProvider>
    </Security>
  )
}

const getComponent = (C) => withRouter(withAnalytics(C))

export default compose(withRouter, withEmbedAuthentication)(Routes)
